import { create } from "zustand";
import type {
  ArcgisLayerConfig,
  AssetFieldConfig,
  LayerFormData,
} from "@/types/layers";
import { useModalStore } from "./modalStore";

interface LayerStore {
  // Layer state (modal states moved to modalStore)
  newLayerData: LayerFormData & { asset_fields: AssetFieldConfig[] }; // Always ensure array
  validationError: string | null;

  // Actions
  setNewLayerData: (data: LayerFormData) => void;
  setValidationError: (error: string | null) => void;

  // Composite actions
  startCreateLayer: () => void;
  startEditLayer: (layer: ArcgisLayerConfig) => void;
  cancelLayerForm: () => void;
  resetLayerForm: () => void;
  validateLayerData: () => boolean;

  // Asset field management
  addAssetField: () => void;
  removeAssetField: (index: number) => void;
  updateAssetField: (index: number, field: AssetFieldConfig) => void;
}

export const useLayerStore = create<LayerStore>((set, get) => ({
  // Initial state (modal states removed - managed by modalStore)
  newLayerData: {
    key: "",
    label: "",
    url: "",
    layer_numbers: [],
    visible: true,
    task_enabled: true,
    asset_fields: [],
    skip_asset_allowed: true,
    service_type: "mapservice",
  },
  validationError: null,

  // Basic setters
  setNewLayerData: (data) =>
    set({
      newLayerData: {
        ...data,
        asset_fields: data.asset_fields || [],
      },
    }),
  setValidationError: (error) => set({ validationError: error }),

  // Composite actions
  startCreateLayer: () => {
    set({
      newLayerData: {
        key: "",
        label: "",
        url: "",
        layer_numbers: [],
        visible: true,
        task_enabled: true,
        asset_fields: [],
        skip_asset_allowed: true,
        service_type: "mapservice",
      },
      validationError: null,
    });

    // Open the create layer modal
    useModalStore.getState().openCreateLayer();
  },

  startEditLayer: (layer) =>
    set({
      newLayerData: {
        key: layer.key,
        label: layer.label,
        url: layer.url,
        layer_numbers: layer.layer_numbers,
        visible: layer.visible,
        task_enabled: layer.task_enabled,
        asset_fields: layer.asset_fields || [],
        skip_asset_allowed: layer.skip_asset_allowed,
        service_type: layer.service_type,
      },
      validationError: null,
    }),

  cancelLayerForm: () =>
    set({
      newLayerData: {
        key: "",
        label: "",
        url: "",
        layer_numbers: [],
        visible: true,
        task_enabled: true,
        asset_fields: [],
        skip_asset_allowed: true,
        service_type: "mapservice",
      },
      validationError: null,
    }),

  resetLayerForm: () =>
    set({
      newLayerData: {
        key: "",
        label: "",
        url: "",
        layer_numbers: [],
        visible: true,
        task_enabled: true,
        asset_fields: [],
        skip_asset_allowed: true,
        service_type: "mapservice",
      },
      validationError: null,
    }),

  // Validation function
  validateLayerData: () => {
    const { newLayerData } = get();

    if (!newLayerData.key.trim()) {
      set({ validationError: "Layer key is required" });
      return false;
    }

    if (!newLayerData.label.trim()) {
      set({ validationError: "Layer label is required" });
      return false;
    }

    if (!newLayerData.url.trim()) {
      set({ validationError: "Layer URL is required" });
      return false;
    }

    // Only require layer numbers for map services
    if (
      newLayerData.service_type === "mapservice" &&
      newLayerData.layer_numbers.length === 0
    ) {
      set({
        validationError:
          "At least one layer number is required for Map Services",
      });
      return false;
    }

    set({ validationError: null });
    return true;
  },

  // Asset field management
  addAssetField: () => {
    const { newLayerData } = get();

    const newField: AssetFieldConfig = {
      field: "",
      label: "",
      property_key: "",
      extract_pattern: undefined,
    };

    set({
      newLayerData: {
        ...newLayerData,
        asset_fields: [...newLayerData.asset_fields, newField],
      },
    });
  },

  removeAssetField: (index) => {
    const { newLayerData } = get();
    set({
      newLayerData: {
        ...newLayerData,
        asset_fields: newLayerData.asset_fields.filter((_, i) => i !== index),
      },
    });
  },

  updateAssetField: (index, field) => {
    const { newLayerData } = get();
    const updatedFields = [...newLayerData.asset_fields];
    updatedFields[index] = field;
    set({
      newLayerData: {
        ...newLayerData,
        asset_fields: updatedFields,
      },
    });
  },
}));
